/**
 * Tiny fetch wrapper around the backend API. Pulls the current ID token
 * from the OIDC user store via a getter so we don't have to thread auth
 * state through every component.
 *
 * Both data and admin routes sit behind one unified API Gateway HTTP
 * API (provisioned by aws-setup/api/apply-api.sh) with a shared Cognito
 * JWT authorizer. Everything goes to VITE_API_BASE.
 */

const API_BASE = import.meta.env.VITE_API_BASE;

let tokenGetter: () => string | undefined = () => undefined;

export function setTokenGetter(fn: () => string | undefined) {
  tokenGetter = fn;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenGetter();
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // not JSON
    }
    throw new ApiError(
      typeof body === 'object' && body && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText,
      res.status,
      body,
    );
  }
  // No-content responses
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
